import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useToggle } from "usehooks-ts";

import truckEmpty from "assets/images/truck-empty.png";
import Button from "components/Button";
import ConfirmationModal from "components/ConfirmationModal";
import Input from "components/Input";
import Select from "components/Select";
import { COMPANY_TYPE } from "constants/haulpay";
import { usePullToRefresh } from "context/PullToRefreshContext";
import AddCustomerModal from "features/main/HaulPay/modules/Shared/AddCustomerModal";
import CreditItem from "features/main/HaulPay/modules/Shared/CreditItem";
import CreditItemSkeleton from "features/main/HaulPay/modules/Shared/CreditItem/CreditItemSkeleton";
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";
import useDeviceType from "hooks/useDeviceType";
import { ICheckCredit } from "models/HaulPay";
import {
  addCustomerAPI,
  factoringDebtorAPI,
  getGenericNextPageAPI,
  sendNoaAPI
} from "services/api/haulpay";
import styles from "./style.module.scss";
import { useAnalytics } from "../../../../../../hooks/useAnalytics";

export default function CheckCredit() {
  const { isDesktop } = useDeviceType();
  const ContainerRef = useRef<HTMLFormElement>(null);
  const { trackAllEvents } = useAnalytics();
  const [isAddModalOpen, toggleAddModal] = useToggle();
  const [isCheckReviewModalOpen, toggleCheckReviewModal] = useToggle();

  const [nextLink, setNextLink] = useState("nextLink");
  const [prevLink, setPrevLink] = useState("prevLink");
  const [debtors, setDebtors] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchFilter, setSearchFilter] = useState<any>();
  const [selectedData, setSelectedData] = useState<{
    id: string;
    credit_approved: string;
    dba: string;
  }>({
    id: "",
    credit_approved: "",
    dba: ""
  });

  const fetchFactoringDebtors = async () => {
    setIsLoading(true);
    try {
      const filter = {
        limit: 20,
        ...searchFilter
      };

      const response = await factoringDebtorAPI(filter);

      setNextLink(response.data.next);
      setPrevLink(response.data.previous);
      setDebtors(response.data.results);
    } catch {
      showErrorMessage("Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  usePullToRefresh(fetchFactoringDebtors, { enabled: !!searchFilter || debtors.length > 0 });

  useEffect(() => {
    if (searchFilter) {
      fetchFactoringDebtors();
    }
  }, [searchFilter]);

  const {
    register,
    control,
    formState: { isSubmitting },
    handleSubmit
  } = useForm<Partial<ICheckCredit>>({
    values: {
      debtorCompanyType: COMPANY_TYPE[0]
    }
  });

  const addCustomer = async (credit_approved: string, id: string) => {
    if (
      credit_approved === "declined" ||
      credit_approved === "needs_review" ||
      credit_approved === "approved"
    ) {
      sendNoa(false, id, credit_approved);
    } else {
      const formData = {
        id: id,
        credit_approved: "needs_review"
      };
      try {
        await addCustomerAPI(formData);
        showSuccessMessage("Successfully added customer.");
        fetchFactoringDebtors();
      } catch (error: any) {
        showErrorMessage(error.message);
      }
    }
    trackAllEvents("haulpay_credit_check_add_customer");
  };

  const sendNoa = async (isNoaSent: boolean, debtorId: string, creditApproved: string) => {
    const formData = {
      auto_send_noa: isNoaSent,
      debtor: debtorId
    };
    try {
      await sendNoaAPI(formData);
      showSuccessMessage("Successfully added customer.");
      fetchFactoringDebtors();
      if (creditApproved !== "declined") toggleCheckReviewModal();
    } catch (error: any) {
      showErrorMessage(error.message);
    }
  };

  const handlePageChange = async (itemsPerPage: number) => {
    try {
      let response: any;
      if (itemsPerPage > 0) {
        response = await getGenericNextPageAPI(
          nextLink ??
            `/factoring/debtors/?dba=0&mc=0&dot=0&tax_id=0&factoring=0&limit=${itemsPerPage}&company_name=0`
        );
      } else {
        response = await getGenericNextPageAPI(
          prevLink ??
            `/factoring/debtors/?dba=0&mc=0&dot=0&tax_id=0&factoring=0&limit=${-itemsPerPage}&company_name=0`
        );
      }
      setNextLink(response.data.next);
      setPrevLink(response.data.previous);
      setDebtors([...debtors, ...response.data.results]);
    } catch {
      showErrorMessage("Please try again later.");
    }
  };

  const onScroll = async () => {
    if (ContainerRef.current) {
      const { scrollTop, offsetHeight, scrollHeight } = ContainerRef.current;
      if (!!scrollTop && scrollTop + offsetHeight + 1 >= scrollHeight && nextLink) {
        handlePageChange(20);
      }
    }
  };

  const onSubmit = (data: any) => {
    const formData = {
      dba: data.debtorCompanyName,
      [data.debtorCompanyType.value]: data.debtorCompanyNumber
    };
    trackAllEvents("haulpay_credit_check_search_credit");
    setSearchFilter(formData);
  };

  return (
    <form
      className={styles.checkCreditContainer}
      onSubmit={handleSubmit(onSubmit)}
      ref={ContainerRef}
      onScroll={onScroll}>
      <div className="flex items-center justify-between">
        <span className="text-sm text-text desktop:text-base">
          You can check companies credit for free.
        </span>
        {isDesktop ? (
          <Button type="button" variant="outline" onClick={toggleAddModal}>
            New Customer
          </Button>
        ) : null}
      </div>

      <div className={styles.customerWrapper}>
        <Input
          name="debtorCompanyName"
          register={register}
          label="&nbsp;&nbsp;Company Name"
          className="desktop:w-1/4"
        />

        <div className={styles.companyName}>
          {isDesktop ? <span>Or</span> : null}
          <Controller
            name="debtorCompanyType"
            control={control}
            render={({ field: { value = COMPANY_TYPE[0], onChange } }) => (
              <Select
                options={COMPANY_TYPE}
                value={value}
                onChange={onChange}
                {...(!isDesktop && { label: "Or" })}
                {...(!isDesktop && { labelClass: "pl-1.5" })}
                className="w-28 desktop:w-40"
              />
            )}
          />
          <Input
            name="debtorCompanyNumber"
            register={register}
            className="flex-1"
            {...(!isDesktop && { label: "debtor" })}
            {...(!isDesktop && { labelClass: "opacity-0" })}
          />
        </div>

        <div className={styles.customerFooter}>
          {!isDesktop ? (
            <Button type="button" variant="outline" onClick={toggleAddModal}>
              New Customer
            </Button>
          ) : null}
          <Button type="submit" loading={isSubmitting}>
            Search Credit
          </Button>
        </div>
      </div>

      {isLoading && debtors.length === 0 ? (
        <>
          {[...Array(3)].map((_, index) => (
            <CreditItemSkeleton key={index} />
          ))}
        </>
      ) : debtors.length ? (
        debtors.map((item) => (
          <CreditItem
            key={item.id}
            data={item}
            addCustomer={addCustomer}
            setSelectedData={setSelectedData}
            onSearch={true}
          />
        ))
      ) : searchFilter ? (
        <div className={styles.checkCreditEmpty}>
          <img src={truckEmpty} alt="Empty truck list" />
          <h3 className={styles.checkCreditEmptyTitle}>No results found.</h3>
          <p className="font-medium text-center">Try refining your search to view results.</p>
        </div>
      ) : null}

      <AddCustomerModal
        isAddModalOpen={isAddModalOpen}
        toggleAddModal={toggleAddModal}
        updateCustomerList={fetchFactoringDebtors}
      />

      <ConfirmationModal
        title={`You are now connected to debtor ${selectedData.dba}`}
        isHideHeaderIcon
        content="You can find them in your My Customer section. Once approved, you will have the option to send your NOA in advance by clicking the Not Sent (Click to Send) button."
        isConfirmModalOpen={isCheckReviewModalOpen}
        toggleConfirmModal={toggleCheckReviewModal}
        labelOKButton="Ok"
        isHideCancelButton
      />
    </form>
  );
}
